import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import Members from "../components/Members";
import { Box } from "@mui/material";
import Labels from "../labels/Labels";

export const MEMBERS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const MEMBERS_PAGE_SIZE_STORAGE_KEY = "queryMembers.pageSize";

function getInitialPageSize() {
  try {
    const stored = Number(
      window.localStorage.getItem(MEMBERS_PAGE_SIZE_STORAGE_KEY)
    );
    if (MEMBERS_PAGE_SIZE_OPTIONS.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage unavailable; fall back to default
  }
  return MEMBERS_PAGE_SIZE_OPTIONS[0];
}

export default function QueryMembers() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(getInitialPageSize);
  const [filterString, setFilterString] = useState("");
  const [underReviewOnly, setUnderReviewOnly] = useState(false);
  const [employerId, setEmployerId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [creationPeriod, setCreationPeriod] = useState(null);
  const [orderSubStatus, setOrderSubStatus] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [automaticRefresh, setAutomaticRefresh] = useState(false);
  const refreshInterval = automaticRefresh ? 10000 : false;

  async function fetchMembers(
    page,
    pageSize,
    filterString,
    underReviewOnly,
    employerId,
    orderStatus,
    orderSubStatus,
    storeId,
    creationPeriod
  ) {
    let url =
      window.API_URL +
      `/pad/members?pageSize=` +
      pageSize +
      `&page=` +
      page +
      `&filter=` +
      filterString +
      `&underReviewOnly=` +
      underReviewOnly;
    if (employerId) {
      url += `&employerId=` + employerId;
    }
    if (orderStatus) {
      url += `&orderStatus=` + encodeURIComponent(orderStatus);
    }
    if (orderSubStatus) {
      url += `&orderSubStatus=` + encodeURIComponent(orderSubStatus);
    }
    if (storeId) {
      url += `&storeId=` + encodeURIComponent(storeId);
    }
    if (creationPeriod) {
      url += `&creationPeriod=` + encodeURIComponent(creationPeriod);
    }
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  async function fetchMembersCount(
    filterString,
    underReviewOnly,
    employerId,
    orderStatus,
    orderSubStatus,
    storeId,
    creationPeriod
  ) {
    let url =
      window.API_URL +
      `/pad/members/count?filter=` +
      filterString +
      `&underReviewOnly=` +
      underReviewOnly;
    if (employerId) {
      url += `&employerId=` + employerId;
    }
    if (orderStatus) {
      url += `&orderStatus=` + encodeURIComponent(orderStatus);
    }
    if (orderSubStatus) {
      url += `&orderSubStatus=` + encodeURIComponent(orderSubStatus);
    }
    if (storeId) {
      url += `&storeId=` + encodeURIComponent(storeId);
    }
    if (creationPeriod) {
      url += `&creationPeriod=` + encodeURIComponent(creationPeriod);
    }
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  const { data, dataUpdatedAt, isFetching, isLoading, isError, refetch } = useQuery(
    [
      "members",
      page,
      pageSize,
      filterString,
      underReviewOnly,
      employerId,
      orderStatus,
      orderSubStatus,
      storeId,
      creationPeriod,
    ],
    () =>
      fetchMembers(
        page,
        pageSize,
        filterString,
        underReviewOnly,
        employerId,
        orderStatus,
        orderSubStatus,
        storeId,
        creationPeriod
      ),
    {
      keepPreviousData: true,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const {
    data: dataCount,
    dataUpdatedAt: countUpdatedAt,
    isFetching: isFetchingDataCount,
    isLoading: isLoadingDataCount,
    isError: isErrorDataCount,
    refetch: refetchDataCount,
  } = useQuery(
    [
      "membersCount",
      filterString,
      underReviewOnly,
      employerId,
      orderStatus,
      orderSubStatus,
      storeId,
      creationPeriod,
    ],
    () =>
      fetchMembersCount(
        filterString,
        underReviewOnly,
        employerId,
        orderStatus,
        orderSubStatus,
        storeId,
        creationPeriod
      ),
    {
      keepPreviousData: true,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  return (
    <Box sx={{ margin: "0" }}>
      <Members
        isFetching={isFetching}
        isLoading={isLoading}
        isError={isError}
        data={data}
        dataUpdatedAt={dataUpdatedAt}
        countUpdatedAt={countUpdatedAt}
        refreshInterval={refreshInterval}
        automaticRefresh={automaticRefresh}
        setAutomaticRefreshFunction={setAutomaticRefresh}
        refetchFunction={refetch}
        page={page}
        setPageFunction={setPage}
        pageSize={pageSize}
        pageSizeOptions={MEMBERS_PAGE_SIZE_OPTIONS}
        setPageSizeFunction={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
          try {
            window.localStorage.setItem(
              MEMBERS_PAGE_SIZE_STORAGE_KEY,
              String(newPageSize)
            );
          } catch (e) {
            // ignore persistence failures
          }
        }}
        setFilterStringFunction={setFilterString}
        setUnderReviewOnlyFunction={setUnderReviewOnly}
        setEmployerFilterFunction={(id) => {
          setEmployerId(id);
          setStoreId(null);
          setPage(0); // Reset to first page when employer changes
        }}
        setStoreFilterFunction={(id) => {
          setStoreId(id || null);
          setPage(0);
        }}
        setOrderStatusFilterFunction={(status, period) => {
          setOrderStatus(status === "PASSED" ? "PASSED_STATISTIC" : status);
          setCreationPeriod(period !== "allTime" ? period : null);
          if (status !== "KYC_MANUAL") {
            setOrderSubStatus(null);
          }
          setPage(0);
        }}
        setOrderSubStatusFilterFunction={(subStatus) => {
          setOrderSubStatus(subStatus);
          setPage(0);
        }}
        dataCount={dataCount}
        isFetchingDataCount={isFetchingDataCount}
        isLoadingDataCount={isLoadingDataCount}
        isErrorDataCount={isErrorDataCount}
        refetchDataCount={refetchDataCount}
        title={Labels.getLabel(
          "employer.members-workers.title",
          "Members/Workers"
        )}
      />
    </Box>
  );
}
